/**
 * @file router/index.ts
 * @description 路由配置文件，定义应用的所有路由规则和导航守卫
 */

import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/store/user'
import MainLayout from '@/layouts/Index.vue' // 主布局组件
import NProgress from 'nprogress' // 页面加载进度条
import 'nprogress/nprogress.css' // 进度条样式

/**
 * 路由配置数组
 * @description 定义应用中所有的路由规则
 *
 * meta 字段说明：
 * - title: 页面标题，用于面包屑和标签页显示
 * - noAuth: 是否无需登录权限，true 表示无需登录即可访问
 * - breadcrumb: 是否在面包屑中显示，false 表示不显示
 */
const routes: RouteRecordRaw[] = [
  // ==================== 默认重定向 ====================
  {
    path: '/',
    name: 'HomePage',
    meta: {
      title: '官网',
      noAuth: true,
    },
    component: () => import('@/views/Home/Index.vue') // 注册页面
  },
  // ==================== 错误页面 ====================
  {
    path: '/404',
    meta: {
      title: '404',
      noAuth: true,
    },
    component: () => import(/* webpackChunkName: "404" */ '../views/pages/errorPages/404.vue'), // 404 错误页面
  },
  // 通配路由：所有未匹配的路径重定向到 404 页面
  { path: '/:path(.*)', redirect: '/404' },
]

/**
 * 创建路由实例
 * 使用 Hash 模式（URL 中带 # 号）
 */
const router = createRouter({
  history: createWebHashHistory(), // Hash 路由模式
  routes // 路由配置
})

/**
 * 全局前置导航守卫
 * @description 在每次路由切换前执行，用于权限验证
 * @param to - 目标路由对象
 * @param from - 当前路由对象
 * @param next - 导航控制函数
 */
router.beforeEach((to, from, next) => {
  NProgress.start() // 开始加载进度条

  const userStore = useUserStore()

  // 获取用户信息（从 sessionStorage）
  let token = sessionStorage.getItem('token')

  // 如果目标页面不需要权限验证，则清除 token和用户信息
  if (to.meta.noAuth === true && to.path !== '/404') {
    userStore.clearToken()
  }

  // 权限验证：如果未登录且目标页面需要权限，则跳转到登录页
  if (!token && to.meta.noAuth !== true) {
    next('/login')
  } else {
    next() // 允许导航
  }
})

/**
 * 全局后置导航守卫
 * @description 在每次路由切换完成后执行
 */
router.afterEach(() => {
  NProgress.done() // 结束加载进度条
})

export default router
